import React, { Component } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import ReactGA from "react-ga";
import "url-search-params-polyfill";
import Loadable from "react-loadable";

import "styles/App.css";

import EnvironmentService from "service/environment/EnvironmentService";
import EnvironmentConfig from "service/environment/EnvironmentConfig";
import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import Helper from "util/Helper";

import ProtectedRoute from "components/route/ProtectedRoute";
import ScrollToTop from "components/common/ScrollToTop";
import GlobalError from "components/common/GlobalError";

import Drawer from "components/drawer/Drawer";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import NoPageFound from "modules/NoPageFound";
import DoctorDrawer from "components/drawer/DoctorDrawer";
import NavigoDashboard from "./modules/misc/modules/navigo-dashboard";
import ClevertapReact from "clevertap-react";
import clevertap from "clevertap-web-sdk";
import { jwtDecode } from "jwt-decode";
import ClaimsDashboard from "./modules/misc/modules/navigo-dashboard/components/claims-dashboard";
import ClaimsDashboardFaqs from "./modules/misc/modules/navigo-dashboard/components/claims-dashboard-faqs";
import ClaimAIReview from "./modules/misc/modules/navigo-dashboard/components/claim-ai-review";
import ClaimAIDashboard from "./modules/misc/modules/navigo-dashboard/components/claim-ai-dashboard";
import FileNewClaimScreen from "./modules/misc/modules/navigo-dashboard/components/FileNewClaimScreen";
import ServiceModule from "./modules/misc/modules/service-module";
import AccountModule from "./modules/misc/modules/account-module";
import NavigoModule from "./modules/misc/modules/navigo-module";
import AssignedAccountsOnPid from "./modules/misc/modules/assigned-module/components/AssignedAccountsOnPid";
import AssignedPlansOnPid from "./modules/misc/modules/assigned-module/components/AssignedPlansOnPid";
import AssignAccountsOnPid from "./modules/misc/modules/assigned-module/components/AssignAccountsOnPid";
import AssignPlansOnPid from "./modules/misc/modules/assigned-module/components/AssignPlansOnPid";
import DigitisationModule from "./modules/misc/modules/document-digitisation";
import DisqaulificationModule from "./modules/misc/modules/disqualification-module";
import RefundModule from "./modules/misc/modules/refund-module";
import CartModule from "./modules/misc/modules/cart-module";
import MarginModule from "./modules/misc/modules/slot-margin";
import RiskStratification from "./modules/misc/modules/risk-stratification";
import LTR from "./modules/misc/modules/ltr";

const LoadingComponent = (props) => {
  if (props.error)
    return EventService.emit("showError", {
      message: "Error loading the module",
    });

  if (props.pastDelay) return <h3> loading... </h3>;
  return null;
};
const Employee = Loadable({
  loader: () => import("modules/misc/modules/employee"),
  loading: LoadingComponent,
});
const MedicineOrders = Loadable({
  loader: () => import("modules/ops/modules/medicine-orders"),
  loading: LoadingComponent,
});
const PdfDialog = Loadable({
  loader: () =>
    import("./modules/ops/modules/patients/modules/new-bridge/modules/detailed-assessment/components/PdfDialog"),
  loading: LoadingComponent,
});
const HealthGoals = Loadable({
  loader: () => import("./modules/misc/modules/health-goals"),
  loading: LoadingComponent,
});

const Escalations = Loadable({
  loader: () => import("modules/ops/modules/escalations"),
  loading: LoadingComponent,
});

const SelectTenant = Loadable({
  loader: () => import("modules/user/components/SelectTenant"),
  loading: LoadingComponent,
});

const Login = Loadable({
  loader: () => import("modules/user/components/Login"),
  loading: LoadingComponent,
});
const DoctorLogin = Loadable({
  loader: () => import("modules/user/components/LoginAsDoctor"),
  loading: LoadingComponent,
});

const Profile = Loadable({
  loader: () => import("modules/user/components/Profile"),
  loading: LoadingComponent,
});

const Dashboard = Loadable({
  loader: () => import("modules/dashboard/components/Dashboard"),
  loading: LoadingComponent,
});

const Doctors = Loadable({
  loader: () => import("modules/sales/modules/doctors/components/Doctors"),
  loading: LoadingComponent,
});

const CareManager = Loadable({
  loader: () => import("modules/ops/modules/care-manager"),
  loading: LoadingComponent,
});

const Consultations = Loadable({
  loader: () =>
    import("modules/ops/modules/consultations/components/Consultations"),
  loading: LoadingComponent,
});

const Feedbacks = Loadable({
  loader: () => import("modules/ops/modules/feedbacks/components/Feedbacks"),
  loading: LoadingComponent,
});

const FeedbackDashboard = Loadable({
  loader: () =>
    import("modules/ops/modules/feedbacks/components/FeedbackDashboard"),
  loading: LoadingComponent,
});

const Communications = Loadable({
  loader: () =>
    import("modules/ops/modules/communications/components/Communications"),
  loading: LoadingComponent,
});

const Patient = Loadable({
  loader: () => import("modules/ops/modules/patients/components/Patient"),
  loading: LoadingComponent,
});

const Miscellaneous = Loadable({
  loader: () => import("modules/misc/components/Miscellaneous"),
  loading: LoadingComponent,
});

const Units = Loadable({
  loader: () => import("modules/misc/modules/units/components/Units"),
  loading: LoadingComponent,
});

const Cuisines = Loadable({
  loader: () => import("modules/misc/modules/cuisines/components/Cuisines"),
  loading: LoadingComponent,
});

const Params = Loadable({
  loader: () => import("modules/misc/modules/params/components/Params"),
  loading: LoadingComponent,
});

const ParamGroups = Loadable({
  loader: () => import("modules/misc/modules/params/modules/groups"),
  loading: LoadingComponent,
});

const GoalsMaster = Loadable({
  loader: () => import("modules/misc/modules/goals/components/GoalsMaster"),
  loading: LoadingComponent,
});

const TasksMaster = Loadable({
  loader: () => import("modules/misc/modules/tasks/components/TasksMaster"),
  loading: LoadingComponent,
});

const ACL = Loadable({
  loader: () => import("modules/misc/modules/acl/components/ACL"),
  loading: LoadingComponent,
});

const InformationCards = Loadable({
  loader: () =>
    import("modules/ops/modules/information-cards/components/InformationCards"),
  loading: LoadingComponent,
});

const AddEditInformationCard = Loadable({
  loader: () =>
    import("modules/ops/modules/information-cards/components/AddEditInformationCard"),
  loading: LoadingComponent,
});

const ChecklistMasters = Loadable({
  loader: () =>
    import("modules/misc/modules/checklists/components/ChecklistMasters"),
  loading: LoadingComponent,
});

const ManagePatientTracking = Loadable({
  loader: () =>
    import("modules/ops/modules/patients/modules/tracking/components/ManagePatientTracking"),
  loading: LoadingComponent,
});

const PatientTracking = Loadable({
  loader: () =>
    import("modules/ops/modules/patients/modules/tracking/components/PatientTracking"),
  loading: LoadingComponent,
});

const DoctorBridgeView = Loadable({
  loader: () =>
    import("modules/ops/modules/patients/modules/doctor-bridge/index"),
  loading: LoadingComponent,
});

const ReportsAndVitalsCareManager = Loadable({
  loader: () => import("modules/ops/modules/patients/modules/new-bridge/index"),
  loading: LoadingComponent,
});

const ReportsAndVitals = Loadable({
  loader: () => import("modules/ops/modules/patients/modules/new-bridge/index"),
  loading: LoadingComponent,
});

const AddEditEmployee = Loadable({
  loader: () => import("modules/user/components/AddEditEmployee"),
  loading: LoadingComponent,
});

const Games = Loadable({
  loader: () => import("modules/recreation/modules/games"),
  loading: LoadingComponent,
});
const Protocols = Loadable({
  loader: () => import("modules/ops/modules/Protocols/Proto"),
  loading: LoadingComponent,
});

const DiseasesProtocols = Loadable({
  loader: () => import("modules/ops/modules/Protocols/Pages/DiseasesProtocols"),
  loading: LoadingComponent,
});
const SymptomsProtocols = Loadable({
  loader: () => import("modules/ops/modules/Protocols/Pages/SymptomsProtocols"),
  loading: LoadingComponent,
});
const AcuteconditionsProtocols = Loadable({
  loader: () =>
    import("modules/ops/modules/Protocols/Pages/AcuteconditionsProtocols"),
  loading: LoadingComponent,
});
const CareManagerProtocols = Loadable({
  loader: () =>
    import("modules/ops/modules/Protocols/Pages/CareManagerProtocols"),
  loading: LoadingComponent,
});

const MedicalConditions = Loadable({
  loader: () =>
    import("./modules/misc/modules/medicalConditions/components/MedicalConditions"),
  loading: LoadingComponent,
});

const Constituents = Loadable({
  loader: () =>
    import("./modules/misc/modules/constituents/components/Constituents"),
  loading: LoadingComponent,
});

const DishesMaster = Loadable({
  loader: () => import("./modules/misc/modules/dishes/components/DishesMaster"),
  loading: LoadingComponent,
});

const Sales = Loadable({
  loader: () => import("modules/sales/modules/patients/"),
  loading: LoadingComponent,
});

const Patients = Loadable({
  loader: () => import("modules/ops/modules/patients/"),
  loading: LoadingComponent,
});

/* Patients REVAMP (Figma 4591:3). Mounted alongside the original on
   /patients-v2 — see modules/ops/modules/patients-v2. Reverting to the old
   screen is deleting this Loadable and its <ProtectedRoute> below; nothing in
   the original module was touched. */
const PatientsV2 = Loadable({
  loader: () => import("modules/ops/modules/patients-v2/"),
  loading: LoadingComponent,
});

/* Plan & account details REVAMP (Figma 4654:2) — merges the legacy
   /assigned-accounts/:pid and /assigned-plans/:pid screens into one page.
   Both old routes and their module stay exactly as they were. */
/* Patient Profile revamp — Figma 4661:4. Same containment as the two screens
   above: a new route over the SAME APIs the legacy new-bridge screens use, so
   reverting is deleting this Loadable and its <ProtectedRoute>. */
const ProfileV2 = Loadable({
  loader: () => import("modules/ops/modules/profile-v2/"),
  loading: LoadingComponent,
});

const PlanAccountV2 = Loadable({
  loader: () => import("modules/misc/modules/plan-account-v2/"),
  loading: LoadingComponent,
});

/* Patient health record — the phr patient dashboard, vendored into
   modules/patient and mounted here. Code-split, so nothing of it is fetched
   unless this route is visited. Reverting is deleting this Loadable, its
   <ProtectedRoute> below, and the modules/patient directory; the legacy
   /patients/:patientId/new-bridge screen is untouched and still reachable. */
/* Appointments, PATIENT-SCOPED — Figma 4799:370. A new route over the SAME
   /v1/appointment/filter endpoint the legacy navigo dashboards already call;
   /miscellaneous/navigo-dashboard and /navigo-module are untouched. Reverting
   is deleting this Loadable, its <ProtectedRoute> below, and the folder. */
const AppointmentsV2 = Loadable({
  loader: () => import("modules/ops/modules/appointments-v2/"),
  loading: LoadingComponent,
});

const PatientRecordV2 = Loadable({
  loader: () => import("modules/patient/"),
  loading: LoadingComponent,
});

/* The v4 health-plan editor, opened from the patient record's header. Lives
   inside modules/patient so the whole port stays deletable as one directory.
   PATIENT-keyed: phr routes it by appointment id, but that id only ever
   resolved a patientId — see the component's own header comment. */
const PatientHealthPlanV2 = Loadable({
  loader: () => import("modules/patient/healthplan/components/HealthPlan"),
  loading: LoadingComponent,
});

/* The revamped Create Rx, opened from the patient record's header. Same
   arrangement as the health-plan editor: inside modules/patient so the port
   stays deletable as one directory, and PATIENT-keyed where phr routes it by
   appointment id. Bridge's own /prescription/:userId below is untouched and
   still reachable from the legacy patient profile. */
const PatientPrescriptionV2 = Loadable({
  loader: () => import("modules/patient/prescription/components/ReviewPrescription"),
  loading: LoadingComponent,
});

const DoctorPatients = Loadable({
  loader: () => import("modules/ops/modules/patients/DoctorPatient"),
  loading: LoadingComponent,
});

const NewAppFeature = Loadable({
  loader: () => import("./modules/misc/modules/new-app-feature"),
  loading: LoadingComponent,
});

const HealthPlan = Loadable({
  loader: () => import("./modules/misc/modules/health-plan"),
  loading: LoadingComponent,
});
const NewHealthPlanItems = Loadable({
  loader: () => import("./modules/misc/modules/new-health-plan-items"),
  loading: LoadingComponent,
});

const HealthPlanOption = Loadable({
  loader: () => import("./modules/misc/modules/health-plan-option"),
  loading: LoadingComponent,
});
const NewHealthPlanOption = Loadable({
  loader: () => import("./modules/misc/modules/new-health-plan-option"),
  loading: LoadingComponent,
});

const HealthPlanTemplate = Loadable({
  loader: () =>
    import("./modules/ops/modules/patients/modules/new-bridge/modules/health-plan"),
  loading: LoadingComponent,
});
const Prescription = Loadable({
  loader: () =>
    import("./modules/ops/modules/patients/modules/new-bridge/modules/prescription"),
  loading: LoadingComponent,
});
const CouponGenerator = Loadable({
  loader: () => import("./modules/ops/modules/coupon-generator"),
  loading: LoadingComponent,
});
const BdAutomation = Loadable({
  loader: () => import("./modules/ops/modules/bd-automation"),
  loading: LoadingComponent,
});

const PlanAssignment = Loadable({
  loader: () => import("./modules/ops/modules/patients/modules/planAssignment"),
  loading: LoadingComponent,
});

const PatientProgram = Loadable({
  loader: () => import("./modules/ops/modules/patient-program/index"),
  loading: LoadingComponent,
});
const User = Loadable({
  loader: () => import("./modules/misc/modules/user"),
  loading: LoadingComponent,
});
const LabtestBooking = Loadable({
  loader: () => import("./modules/misc/modules/labtest-bookings"),
  loading: LoadingComponent,
});
const DocViewer = Loadable({
  loader: () => import("./modules/ops/modules/DocuementViewer/index"),
  loading: LoadingComponent,
});

const RecipeDashboard = Loadable({
  loader: () => import("./modules/misc/modules/recipe-dashboard"),
  loading: LoadingComponent,
});
const GuidelineDashboard = Loadable({
  loader: () => import("./modules/misc/modules/guidelines-dashboard"),
  loading: LoadingComponent,
});
const MedicineDashboard = Loadable({
  loader: () => import("./modules/misc/modules/medicine-dashboard"),
  loading: LoadingComponent,
});
const MedicineVerification = Loadable({
  loader: () => import("./modules/medicine"),
  loading: LoadingComponent,
});

const Whitelisting = Loadable({
  loader: () => import("../src/modules/misc/modules/whitelisting"),
  loading: LoadingComponent,
});
class App extends Component {
  constructor() {
    super();
    this.environment = this.getEnvironment();
    this.environmentService = new EnvironmentService();
    this.environmentService.set(this.environment);

    this.authenticationService = new AuthenticationService();
    this.helper = new Helper();

    this.search = new URLSearchParams(window.location.search);

    // initialize Google Analytics only if production
    if (this.environmentService.isProduction()) {
      ReactGA.initialize("UA-96177847-2");
      this.logPageView();
    }

    this.state = {
      user: this.authenticationService.getUserProfile(),
      who: this.getWhoLoggedIn(),
    };
  }
  getWhoLoggedIn() {
    const user = this.authenticationService.getUserProfile();
    let who = "EMPLOYEE";
    if (user && user.patientProfile && user.patientProfile.id > 0) {
      who = "PATIENT";
    } else if (user && user.employeeProfile && user.employeeProfile.id > 0) {
      who = "EMPLOYEE";
    } else if (
      user &&
      user.employeeProfile &&
      user.employeeProfile.id > 0 &&
      user.doctorProfile &&
      user.doctorProfile.id > 0
    ) {
      who = "EMPLOYEE";
    } else if (user && user.doctorProfile && user.doctorProfile.id > 0) {
      who = "DOCTOR";
    } else {
      who = "EMPLOYEE";
    }
    return who;
  }
  componentWillMount() {
    this.getWhoLoggedIn();
    this.setState({
      user: this.authenticationService.getUserProfile(),
      who: this.getWhoLoggedIn(),
    });
    // this.initializeCleverTap(this.environment);
    this.initializeCleverTapAB(this.environment);
  }

  initializeCleverTapAB = (env) => {
    const cleverTapProdAB = "6KK-WK4-695Z";
    const cleverTapStagAB = "TEST-7KK-WK4-695Z";
    let clevertapId = "TEST-7KK-WK4-695Z";
    if (env === "production") {
      clevertapId = "6KK-WK4-695Z";
    }
    // ClevertapReact.initialize(clevertapId);
    clevertap.privacy.push({ optOut: false }); // Set the flag to true, if the user of the device opts out of sharing their data
    clevertap.privacy.push({ useIP: false }); // Set the flag to true, if the user agrees to share their IP data
    clevertap.init(clevertapId, "in1"); // Replace with values applicable to you. Refer below
  };

  initializeCleverTap = (env) => {
    const cleverTapProd = "677-954-R66Z";
    const cleverTapStag = "TEST-777-954-R66Z";
    let clevertapId = "TEST-777-954-R66Z";
    if (env === "production") {
      clevertapId = "677-954-R66Z";
    }

    ClevertapReact.initialize(clevertapId);
  };
  getEnvironment() {
    let config = EnvironmentConfig;
    let originUrl = window.location.hostname;

    if (
      window.location.hostname == "dev1.bridge.zyla.in" ||
      window.location.hostname == "dev1-sy-bridge.zyla.in" ||
      window.location.hostname == "dev3-bridge.zyla.in" ||
      window.location.hostname == "staging-sy-bridge.zyla.in"
    ) {
      return "staging";
    }
    if (
      window.location.hostname == "bridge.zyla.in" ||
      window.location.hostname == "bridge.prod.zyla.in" ||
      window.location.hostname == "sy-bridge.zyla.in"
    ) {
      return "production";
    }

    for (var environment in config.vars) {
      let domain = config.vars[environment].domain;
      if (domain == originUrl) {
        return config.environments[environment];
      }
    }
  }

  logPageView() {
    ReactGA.set({ page: "home" });
    ReactGA.pageview("home");
  }

  render() {
    const updateStatus = localStorage.getItem("updateStatus");
    const updateMessage = localStorage.getItem("updateMessage");
    const { who } = this.state;

    return (
      <div className="App">
        <Router>
          <ScrollToTop>
            <div>
              <Switch>
                <Route path="/tenant" component={SelectTenant} />
                <Route path="/login" component={Login} />
                <Route path="/doctor/login" component={DoctorLogin} />
                <Route path="/not-found" exact component={NoPageFound} />
                <ProtectedRoute
                  path="/pdf/:patientId/:userId/:userName/:phone"
                  component={PdfDialog}
                  authenticator={this._pdfDialogAuth}
                  redirectTo={"/login"}
                />
                <Route path="/">
                  <div>
                    {who && who === "DOCTOR" && (
                      <DoctorDrawer>
                        <ProtectedRoute
                          path="/"
                          component={DoctorPatients}
                          authenticator={this._authenticator}
                          redirectTo={"/login"}
                        />
                        <ProtectedRoute
                          path="/me"
                          component={Profile}
                          authenticator={this._authenticator}
                          redirectTo={"/login"}
                        />
                        <ProtectedRoute
                          path="/patients/:patientId/new-bridge"
                          component={DoctorBridgeView}
                          authenticator={this._authenticator}
                          redirectTo={"/login"}
                        />
                      </DoctorDrawer>
                    )}

                    {who && who === "EMPLOYEE" && (
                      <Drawer>
                        <div className="container-main">
                          <ProtectedRoute
                            path="/"
                            component={Dashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/protocols"
                            component={Protocols}
                            authenticator={this._authenticator}
                            redirectTo={"/"}
                          />
                          <ProtectedRoute
                            path="/protocols/diseasesprotocols"
                            component={DiseasesProtocols}
                            authenticator={this._authenticator}
                            redirectTo={"/protocols"}
                          />
                          <ProtectedRoute
                            path="/protocols/symptomsprotocols"
                            component={SymptomsProtocols}
                            authenticator={this._authenticator}
                            redirectTo={"/protocols"}
                          />
                          <ProtectedRoute
                            path="/protocols/caremanagerprotocols"
                            component={CareManagerProtocols}
                            authenticator={this._authenticator}
                            redirectTo={"/protocols"}
                          />
                          <ProtectedRoute
                            path="/protocols/acuteconditionsprotocols"
                            component={AcuteconditionsProtocols}
                            authenticator={this._authenticator}
                            redirectTo={"/protocols"}
                          />
                          <ProtectedRoute
                            path="/me"
                            component={Profile}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/employees/new"
                            component={AddEditEmployee}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/doctors"
                            component={Doctors}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients"
                            component={Patients}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients-v2"
                            component={PatientsV2}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/plan-account/:pid"
                            component={PlanAccountV2}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/profile-v2/:pid"
                            component={ProfileV2}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          {/* Sibling of the routes above, inside the EMPLOYEE
                              <Drawer> — the same branch the patients-v2 screen
                              this is opened from lives in. */}
                          {/* Before the record's own route: ProtectedRoute
                              forces `exact`, but keeping the more specific path
                              first makes the intent obvious. Keyed on patientId
                              so a different patient remounts it fresh. */}
                          <ProtectedRoute
                            path="/patient-record/:patientId/health-plan"
                            render={(props) => (
                              <PatientHealthPlanV2
                                key={props.match.params.patientId}
                                {...props}
                              />
                            )}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          {/* Keyed on the pid so a different patient remounts
                              it fresh rather than inheriting the previous
                              patient's filters and page. */}
                          <ProtectedRoute
                            path="/appointments-v2/:pid"
                            render={(props) => (
                              <AppointmentsV2 key={props.match.params.pid} {...props} />
                            )}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patient-record/:patientId/prescription"
                            render={(props) => (
                              <PatientPrescriptionV2
                                key={props.match.params.patientId}
                                {...props}
                              />
                            )}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          {/* Keyed on patientId, like its two sub-routes above:
                              the record loads in componentDidMount alone, and
                              componentDidUpdate never re-reads on a route
                              change, so an in-place push from one patient to
                              another left the identity band, every section and
                              the header's All documents menu showing the
                              PREVIOUS patient. */}
                          <ProtectedRoute
                            path="/patient-record/:patientId"
                            render={(props) => (
                              <PatientRecordV2 key={props.match.params.patientId} {...props} />
                            )}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/sales"
                            component={Sales}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/consultations"
                            component={Consultations}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/feedbacks"
                            component={Feedbacks}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/feedback-dashboard"
                            component={FeedbackDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/communications/:phoneNo?"
                            component={Communications}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/analytics"
                            component={Consultations}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients/:patientId"
                            component={Patient}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients/:patientId/manage"
                            component={ManagePatientTracking}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients/:patientId/track"
                            component={PatientTracking}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients/:patientId/new-bridge"
                            component={ReportsAndVitals}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/care-manager/:patientId/new-bridge"
                            component={ReportsAndVitalsCareManager}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/care-manager"
                            component={CareManager}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/medicine-orders"
                            component={MedicineOrders}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/information-cards"
                            component={InformationCards}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/information-cards/new"
                            component={AddEditInformationCard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/information-cards/edit/:id"
                            component={AddEditInformationCard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous"
                            component={Miscellaneous}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/health-goals"
                            component={HealthGoals}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/units"
                            component={Units}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/params"
                            component={Params}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/params/groups"
                            component={ParamGroups}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/goals/master"
                            component={GoalsMaster}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/tasks/master"
                            component={TasksMaster}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/checklists/master"
                            component={ChecklistMasters}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/acl"
                            component={ACL}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/cuisines"
                            component={Cuisines}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/escalations"
                            component={Escalations}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/medical_conditions"
                            component={MedicalConditions}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/health-plan"
                            component={HealthPlan}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/new-health-plan-items"
                            component={NewHealthPlanItems}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/new-app-feature"
                            component={NewAppFeature}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/health-plan-options"
                            component={HealthPlanOption}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />

                          <ProtectedRoute
                            path="/miscellaneous/new-health-plan-options"
                            component={NewHealthPlanOption}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/health-plan-template"
                            component={HealthPlanTemplate}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/constituents"
                            component={Constituents}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/dish_master"
                            component={DishesMaster}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/labtest-booking"
                            component={LabtestBooking}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />

                          <ProtectedRoute
                            path="/games"
                            component={Games}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/pdf/:patientId/:userId/:userName"
                            component={PdfDialog}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/acl/employee"
                            component={Employee}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/coupon-generator"
                            component={CouponGenerator}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/bd-automation"
                            component={BdAutomation}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/prescription/:userId"
                            component={Prescription}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/acl/user"
                            component={User}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patients/:userId/assign"
                            component={PlanAssignment}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/patient-program"
                            component={PatientProgram}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/medicine-dashboard"
                            component={MedicineDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/medicine-verification"
                            component={MedicineVerification}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/whitelisting"
                            component={Whitelisting}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/disqualification"
                            component={DisqaulificationModule}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/recipe-dashboard"
                            component={RecipeDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/guideline-dashboard"
                            component={GuidelineDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            exact={false}
                            path="/miscellaneous/risk-stratification"
                            component={RiskStratification}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            exact={false}
                            path="/miscellaneous/ltr"
                            component={LTR}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/navigo-dashboard"
                            component={NavigoDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/claims-dashboard"
                            component={ClaimsDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/service_module"
                            component={ServiceModule}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/account_module"
                            component={AccountModule}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/refund-module"
                            component={RefundModule}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/cart-module"
                            component={CartModule}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/assigned-accounts/:pid"
                            component={AssignedAccountsOnPid}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/assign-account/:pid"
                            component={AssignAccountsOnPid}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/assigned-plans/:pid"
                            component={AssignedPlansOnPid}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/assign-plan/:pid"
                            component={AssignPlansOnPid}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          {/* Keyed on the SEARCH, not a path param.
                              NavigoModule reads view/appId/patientId in its
                              constructor alone (navigo-module/index.js:20-24)
                              and has no componentDidUpdate, so a location
                              change that keeps this pathname and alters only
                              the query string reconciles in place rather than
                              remounting, leaving state.view and state.props at
                              the previous link's values. That is reachable
                              today: in a tab opened on RefundDashboard.js:970's
                              /navigo-module?view=2&appId=<id>, the drawer's own
                              "Appointments" item replaces to a bare
                              /navigo-module (Drawer.js:134) and the details
                              screen keeps rendering under a URL that no longer
                              describes it. The appointments-v2 round trip never
                              hits this — both of its hops change the pathname —
                              so there the key is belt and braces. Same
                              render+key shape as /appointments-v2/:pid. */}
                          <ProtectedRoute
                            path="/navigo-module"
                            render={(props) => (
                              <NavigoModule key={props.location.search} {...props} />
                            )}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/claims-dashboard/faqs"
                            component={ClaimsDashboardFaqs}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/claims-dashboard/ai-review/:claimId/:patientId?"
                            component={ClaimAIReview}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/reimbursement-dashboard"
                            component={ClaimAIDashboard}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                          <ProtectedRoute
                            path="/miscellaneous/reimbursement-dashboard/file-claim"
                            component={FileNewClaimScreen}
                            authenticator={this._authenticator}
                            redirectTo={"/login"}
                          />
                        </div>
                        <ProtectedRoute
                          path="/document-digitisation"
                          component={DigitisationModule}
                          authenticator={this._authenticator}
                          redirectTo={"/login"}
                        />
                        <ProtectedRoute
                          path="/margin-module"
                          component={MarginModule}
                          authenticator={this._authenticator}
                          redirectTo={"/login"}
                        />
                      </Drawer>
                    )}
                    {who && who === "EMPLOYEE" && (
                      <ProtectedRoute
                        path="/patient/:patientId/document/:folder/:startIndex"
                        component={DocViewer}
                        authenticator={this._authenticator}
                        redirectTo={"/login"}
                      />
                    )}
                  </div>
                </Route>
              </Switch>
              <GlobalError />
            </div>
          </ScrollToTop>
        </Router>
      </div>
    );
  }

  _pdfDialogAuth = () => {
    try {
      let url = window.location.pathname.split("/");
      let isError = false;
      if (!url[2] || !url[3] || !url[4] || !url[5]) {
        throw new Error("not enough arguments");
      }
      window.isAutoLogin = true;
      const params = new URLSearchParams(window.location.search);
      let authToken = params.get("authToken");
      let token;
      try {
        token = jwtDecode(authToken);
      } catch (err) {
        isError = true;
      }
      if (token.exp * 1000 < Date.now()) {
        isError = true;
      }

      if (token.exp * 1000 < Date.now()) {
        isError = true;
      }
      // if (token.phoneNo != url[5]) {
      //   isError = true;
      // }
      if (isError) {
        const event = new Event("daError");
        document.dispatchEvent(event);
        window.location.href = "/login";
        return false;
      }
      this.authenticationService.activateSession(url[6]);
      this.authenticationService.setUserProfile({});
      this.authenticationService.setLoggedInCookies();
      return true;
    } catch (err) {
      const event = new Event("daError");
      document.dispatchEvent(event);
      return false;
    }
  };

  _authenticator = () => {
    return this.authenticationService.isLoggedIn();
  };

  _reverseAuthenticator = () => {
    return !this.authenticationService.isLoggedIn();
  };
}

export default App;
